import {
  BodyText,
  Box,
  Flex,
  Heading,
  Strong,
  ToggleGroup,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import dayjs from 'dayjs';
import { FormikProps, useFormikContext } from 'formik';

import {
  ChargeAmount,
  EnergyFuelType,
  EnergyQuote,
  EnergyQuoteType,
} from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { DateFormat } from '@src/types/index';
import { capitalizeFirstLetters } from '@utils/capitalizeFirstLetter';
import { formatCurrency } from '@utils/format';

import { TARIFF_CONTENT_ID } from '../constants';
import { EnergyFormValues } from '../types';
import { getTariffEndDate } from '../utils';
import { EnergyTariffTile } from './EnergyTariffTile';
import { TariffsSkeleton } from './TariffsSkeleton';
import './styles.scss';

interface YourTariffProps {
  variable: EnergyQuote;
  tracker?: Nullable<EnergyQuote>;
  fixed?: Nullable<EnergyQuote>;
  isLoading: boolean;
  fuelType: EnergyFuelType;
  hasSmartmeter: boolean;
}

const YourTariff = ({
  variable,
  tracker,
  fixed,
  isLoading,
  fuelType,
  hasSmartmeter,
}: YourTariffProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitCount,
    setFieldValue,
  }: FormikProps<EnergyFormValues> = useFormikContext();

  const getFieldProps = useFieldProps<EnergyFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const renderContent = () => {
    if (isLoading) {
      return <TariffsSkeleton />;
    }

    if (!(variable || tracker || fixed)) {
      return <Strong>N/A</Strong>;
    }

    const error = errors.tariffType;
    const isTouched = touched.tariffType;

    const smartMeterTariffName =
      values.tariffType === EnergyQuoteType.Fixed
        ? 'Fixed'
        : values.tariffType === EnergyQuoteType.Tracker
          ? 'Tracker'
          : '';
    const showSmartMeterNote = !hasSmartmeter && Boolean(smartMeterTariffName);

    return (
      <div>
        <ToggleGroup
          type="single"
          className="energyTariff__toggleGroup"
          {...getFieldProps('tariffType')}
          id={TARIFF_CONTENT_ID}
          onValueChange={(value: string) =>
            value && setFieldValue('tariffType', value)
          }
        >
          <EnergyTariffTile
            serviceName={capitalizeFirstLetters(variable.name)}
            description="Your energy rates will go up and down as the price cap changes"
            price={formatCurrency(variable.monthlyCost as ChargeAmount)}
            isSelected={values.tariffType === EnergyQuoteType.Variable}
            testId="Variable"
            value={EnergyQuoteType.Variable}
            quote={variable}
            fuelType={fuelType}
          />
          {tracker && (
            <EnergyTariffTile
              serviceName={capitalizeFirstLetters(tracker.name)}
              description="Your rates move with the price cap, but with a guaranteed discount and fixed term."
              price={formatCurrency(tracker.monthlyCost as ChargeAmount)}
              isSelected={values.tariffType === EnergyQuoteType.Tracker}
              testId="Tracker"
              value={EnergyQuoteType.Tracker}
              quote={tracker}
              fuelType={fuelType}
            />
          )}
          {fixed && (
            <EnergyTariffTile
              serviceName={capitalizeFirstLetters(fixed.name)}
              description={`Your energy rates will stay the same until ${dayjs(getTariffEndDate(fixed)).format(DateFormat.DisplayMonthName)}`}
              price={formatCurrency(fixed.monthlyCost as ChargeAmount)}
              isSelected={values.tariffType === EnergyQuoteType.Fixed}
              testId="Fixed"
              value={EnergyQuoteType.Fixed}
              quote={fixed}
              fuelType={fuelType}
            />
          )}
        </ToggleGroup>
        {error && isTouched ? (
          <Box marginTop="200">
            <ValidationText status="invalid">{error}</ValidationText>
          </Box>
        ) : (
          <span className="energyTariff__errorPlaceholder" />
        )}
        {showSmartMeterNote && (
          <BodyText size="sm" color="secondary">
            Our {smartMeterTariffName} tariff requires a smart meter. You can
            book a free smart meter upgrade right after you complete your sign
            up.
          </BodyText>
        )}
      </div>
    );
  };

  return (
    <Flex className="energyTariff__wrapper" direction="column" spacing="lg">
      <Heading
        data-testid="energy-your-tariff-heading"
        className="heading"
        size="lg"
        as="h2"
      >
        Choose your tariff
      </Heading>
      {renderContent()}
    </Flex>
  );
};

export type { YourTariffProps };
export { YourTariff };
